import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useQuoteApi } from 'src/api/db/apiQuote'
import { useOrgUsersApi } from 'src/api/db/apiOrgUsers'
import { queryKeys } from 'src/api/db/queryKeys'
import { useSupabaseClient } from 'src/api/supabase'
import { useOrderNotifications } from 'src/api/server/useOrderNotifications'
import { Column, Row } from 'src/shared/components/Semantic/all'
import {
  APPROVED_QUOTE_STATUS,
  APPROVED_PROOFS_STATUS,
  STARTED_PRODUCTION_STATUS,
  SHIPPING_QUOTE_STATUS,
  DELIVERED_STATUS,
  SALES_APPROVED_QUOTE_STATUS,
  effectiveQuoteStatus,
  isPreSalesApprovalStatus,
} from 'src/shared/consts/quoteStatus'
import { useAuth } from 'src/shared/hooks/authProvider'
import { useIsAdmin } from 'src/shared/hooks/useIsAdmin'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { useToast } from 'src/shared/hooks/use-toast'
import {
  calculateQuoteFooterSubtotals,
  calculateQuoteTotal,
} from 'src/shared/components/OrderTable/utils'
import { OrderTableCurrencyProvider } from 'src/shared/components/OrderTable/currency'
import { BillingDocument } from 'src/pages/_myAccount/Requests/Request/components/BillingDocument'
import { GuestSignupDialog } from 'src/pages/_myAccount/Requests/Request/components/Chat/components/GuestSignupDialog'
import { useRequestContext } from 'src/pages/_myAccount/Requests/Request/hooks/RequestContext'
import {
  MAX_LINE_QUANTITY,
  MIN_LINE_QUANTITY,
  QUOTE_VALID_DAYS,
  chosenOption,
  clearStoredDraft,
  formatProposalDate,
  groupProposalLines,
  isProductQuoteItem,
  readStoredDraft,
  resolveSelection,
  selectedAlternativeIds,
  storeDraft,
  withQuantity,
  type ProposalDraft,
} from '../utils'
import { ProposalLineSection } from './ProposalLineSection'
import { ProposalMockups } from './ProposalMockups'
import { ProposalSummary, type ProposalApprovalState } from './ProposalSummary'
import { ProposalTopBar } from './ProposalTopBar'

const isApprovedStatus = (status: string) =>
  status === APPROVED_QUOTE_STATUS ||
  status === APPROVED_PROOFS_STATUS ||
  status === STARTED_PRODUCTION_STATUS ||
  status === SHIPPING_QUOTE_STATUS ||
  status === DELIVERED_STATUS

const ANON_ORG_NAME = 'Anon'

export const ShareOrderProposal = () => {
  const { t, locale } = useLocalization()
  const { toast } = useToast()
  const { session } = useAuth()
  const isAdmin = useIsAdmin()
  const { publicId, quote, quoteItems, refetchQuote } = useRequestContext()
  const { approveSharedQuote } = useQuoteApi({ skipListQuery: true })
  const orderNotifications = useOrderNotifications()
  const supabase = useSupabaseClient()
  const queryClient = useQueryClient()
  const { orgUser } = useOrgUsersApi()

  const quotePublicId = quote?.public_id ?? publicId ?? ''
  const [draft, setDraft] = useState<ProposalDraft>(() =>
    quotePublicId
      ? readStoredDraft(quotePublicId)
      : { selection: {}, quantities: {} }
  )
  const [signupOpen, setSignupOpen] = useState(false)

  useEffect(() => {
    if (quotePublicId) storeDraft(quotePublicId, draft)
  }, [quotePublicId, draft])

  if (!quote) return null

  // The quoted currency: the quote's organization's (as RequestProvider converts with), not the viewer's.
  const currency = quote.organization?.currency ?? 'SEK'

  const status = effectiveQuoteStatus(quote.status)
  const klayderApproved = !isPreSalesApprovalStatus(status)
  const customerApproved = klayderApproved && isApprovedStatus(status)
  const approvalState: ProposalApprovalState = customerApproved
    ? 'approved'
    : status === SALES_APPROVED_QUOTE_STATUS
      ? 'ready'
      : 'in_review'
  const locked = approvalState === 'approved'

  // Colour is saved on the line itself, which the database lets members of the
  // quote's organization do. Visitors outside it keep the quoted colour.
  const isQuoteOrgMember = Boolean(
    session?.user &&
      quote.org_id &&
      orgUser?.data?.organization_id === quote.org_id
  )
  // A recorded choice keeps its colour even if staff move the order back.
  const hasRecordedChoice = (quoteItems ?? []).some(
    (item) => !!item.customer_selected_at
  )
  const canChangeColour =
    !locked && !isAdmin && isQuoteOrgMember && !hasRecordedChoice

  const saveColour = async (optionId: number, colourId: number) => {
    // Only the colour: prices, quantity and decoration lines stay as quoted.
    const { error } = await supabase
      .from('quote_item')
      .update({ product_variant_color_id: colourId })
      .eq('id', optionId)
      .eq('quote_id', quote.id)
      .select('id')
      .single()
    if (error) {
      toast({
        title: t('Could not change the colour', 'Kunde inte byta färg'),
        description: t('Please try again.', 'Försök igen.'),
        variant: 'destructive',
      })
      throw error
    }
    await queryClient.invalidateQueries({ queryKey: [queryKeys.quoteItems] })
  }

  const allItems = quoteItems ?? []
  const productItems = allItems.filter(isProductQuoteItem)
  const otherItems = allItems.filter((item) => !isProductQuoteItem(item))
  const lines = groupProposalLines(productItems)
  // After approval the page shows what was recorded, not the local draft.
  const selection = resolveSelection(lines, locked ? {} : draft.selection)
  const lineQuantity = (key: string, fallback: number) =>
    locked ? fallback : (draft.quantities[key] ?? fallback)

  const pricedLines = lines.map((line) => {
    const option = chosenOption(line, selection)
    const quantity = lineQuantity(line.key, option.quantity || 1)
    return { line, option, quantity, priced: withQuantity(option, quantity) }
  })
  const chosenItems = pricedLines.map((entry) => entry.priced)
  const pricedItems = [...chosenItems, ...otherItems]
  const subtotals = calculateQuoteFooterSubtotals(pricedItems)
  const total = calculateQuoteTotal(pricedItems)

  const canApprove =
    !isAdmin && approvalState === 'ready' && productItems.length > 0
  const requiresSignup = !session?.user?.email

  const statusLabel =
    approvalState === 'approved'
      ? t('Accepted', 'Godkänt')
      : approvalState === 'ready'
        ? t('Awaiting your approval', 'Väntar på ert godkännande')
        : t('In review', 'Granskas')

  const orgName = quote.organization?.name
  const showOrgName = Boolean(orgName && orgName !== ANON_ORG_NAME)
  const quoteName = quote.name || t('Your proposal', 'Ert förslag')

  // Proposal-specific, set by staff; nothing is shown when it is not set.
  const deliveryEstimate = quote.delivery_estimate?.trim()
  const preparedAt = new Date(quote.created_at)
  const validUntil = new Date(preparedAt)
  validUntil.setDate(validUntil.getDate() + QUOTE_VALID_DAYS)

  const chooseOption = (lineKey: string, optionId: number) => {
    if (locked) return
    setDraft((prev) => ({
      ...prev,
      selection: { ...prev.selection, [lineKey]: optionId },
    }))
  }

  const stepQuantity = (lineKey: string, base: number, delta: 1 | -1) => {
    if (locked) return
    setDraft((prev) => {
      const current = prev.quantities[lineKey] ?? base
      const next = Math.min(
        MAX_LINE_QUANTITY,
        Math.max(MIN_LINE_QUANTITY, current + delta)
      )
      return { ...prev, quantities: { ...prev.quantities, [lineKey]: next } }
    })
  }

  const notifyApproved = async () => {
    try {
      await orderNotifications.mutateAsync({
        kind: 'lifecycle',
        actorRole: 'customer',
        subject:
          t('Quote approved for order ', 'Offert godkänd för order ') +
          `#${quotePublicId}`,
        orderNotification: {
          quotePublicId,
          headerBadge: t('Quote approved', 'Offert godkänd'),
          headerTitle: t(
            'Customer approved the quote',
            'Kunden har godkänt offerten'
          ),
          message: t(
            'The customer approved the quote and requested factory proofs. Click below to view the order.',
            'Kunden har godkänt offerten och begärt korrekturer. Klicka nedan för att visa ordern.'
          ),
          buttonText: t('View order', 'Se order'),
        },
      })
    } catch (error) {
      // The approval itself succeeded; a failed staff email must not undo that for the customer.
      console.error('Failed to send approval notification:', error)
    }
  }

  const handleApprove = async () => {
    if (requiresSignup) {
      setSignupOpen(true)
      return
    }
    if (!quote.id || !quotePublicId) return
    const changedQuantities = Object.fromEntries(
      pricedLines
        .filter(({ option, quantity }) => quantity !== option.quantity)
        .map(({ option, quantity }) => [option.id, quantity])
    )
    try {
      await approveSharedQuote.mutateAsync({
        public_id: quotePublicId,
        quote_id: quote.id,
        selected_item_ids: selectedAlternativeIds(lines, selection),
        quantities: changedQuantities,
        has_alternatives: allItems.some((item) => !!item.slot?.trim()),
      })
    } catch (error) {
      console.error('Failed to approve quote:', error)
      // 22023: the selection RPC refused the choices (e.g. a line without one).
      const invalidSelection =
        (error as { code?: string } | null)?.code === '22023'
      toast({
        variant: 'destructive',
        title: t('Something went wrong', 'Något gick fel'),
        description: invalidSelection
          ? t(
              'Choose one option for each line, then approve again. If this keeps happening, contact the Klayder team.',
              'Välj ett alternativ för varje rad och godkänn igen. Kontakta Klayder-teamet om det fortsätter.'
            )
          : t(
              'Could not approve the proposal. Please try again.',
              'Kunde inte godkänna förslaget. Försök igen.'
            ),
      })
      return
    }
    clearStoredDraft(quotePublicId)
    setDraft({ selection: {}, quantities: {} })
    await notifyApproved()
    refetchQuote()
  }

  return (
    <div className="proposal-page min-h-dvh w-full overflow-x-hidden bg-proposal-ground font-tiktok-sans text-base font-extralight text-proposal-ink">
      <ProposalTopBar showLogin={!session?.user?.email} />

      <div className="mx-auto box-content flex max-w-[1080px] flex-wrap items-start gap-9 px-[5%] pb-16 pt-8 sm:pt-11">
        <main className="flex min-w-0 flex-[999_1_34rem] flex-col gap-[26px]">
          <Column className="gap-2.5">
            <Row className="flex-wrap items-center gap-3">
              <span className="text-[13.5px] text-proposal-ink-muted">
                {showOrgName
                  ? `${t('Proposal for', 'Förslag till')} ${orgName}`
                  : `${t('Proposal', 'Förslag')} #${quotePublicId}`}
              </span>
              <span className="rounded-full bg-proposal-orange/[0.14] px-3 py-1.5 text-xs font-semibold text-proposal-orange-ink">
                {statusLabel}
              </span>
            </Row>
            <h1 className="m-0 text-[28px] font-semibold !leading-tight tracking-[-0.02em] text-proposal-ink">
              {quoteName}
            </h1>
            <Row className="flex-wrap gap-x-[22px] gap-y-1 pt-1.5 text-[13.5px] text-proposal-ink-muted">
              <span>
                {t('Prepared', 'Upprättat')}{' '}
                {formatProposalDate(preparedAt, locale)}
              </span>
              {!locked ? (
                <span>
                  {t('Valid until', 'Giltigt till')}{' '}
                  {formatProposalDate(validUntil, locale)}
                </span>
              ) : null}
              {deliveryEstimate ? (
                <span>
                  {t('Delivery', 'Leverans')} {deliveryEstimate}
                </span>
              ) : null}
            </Row>
          </Column>

          {locked ? (
            // After approval the alternatives give way to the agreed quote:
            // only the approved lines, quantities and prices, read-only.
            <OrderTableCurrencyProvider currency={currency}>
              <BillingDocument
                quote={quote}
                quoteItems={allItems}
                publicId={quotePublicId}
                mode="quote"
                isReadOnly
              />
            </OrderTableCurrencyProvider>
          ) : lines.length === 0 ? (
            <section className="rounded-[20px] border border-[var(--raised-border)] bg-proposal-surface shadow-soft p-6 text-[14.5px] text-proposal-ink-soft">
              {t(
                'We are putting your proposal together. It will appear here shortly.',
                'Vi sätter ihop ert förslag. Det dyker upp här inom kort.'
              )}
            </section>
          ) : (
            pricedLines.map(({ line, option, priced, quantity }) => (
              <ProposalLineSection
                key={line.key}
                line={line}
                chosen={priced}
                quantity={quantity}
                currency={currency}
                locked={locked}
                onChoose={(optionId) => chooseOption(line.key, optionId)}
                onQuantityStep={(delta) =>
                  stepQuantity(line.key, option.quantity || 1, delta)
                }
                canChangeColour={canChangeColour}
                onColourChange={saveColour}
              />
            ))
          )}

          <ProposalMockups items={chosenItems} />
        </main>

        <ProposalSummary
          subtotals={subtotals}
          total={total}
          currency={currency}
          approvalState={approvalState}
          canApprove={canApprove}
          isApproving={approveSharedQuote.isPending}
          onApprove={handleApprove}
        />
      </div>

      {requiresSignup && quotePublicId ? (
        <GuestSignupDialog
          open={signupOpen}
          onOpenChange={setSignupOpen}
          initialEmail=""
          nextPath={`/share-order/${quotePublicId}`}
          title={t('Sign up to approve', 'Skapa konto för att godkänna')}
          description={t(
            'Create a free account on your work email. You come straight back here, with your choices kept, to approve.',
            'Skapa ett gratiskonto med er jobbmejl. Ni kommer direkt tillbaka hit, med era val sparade, för att godkänna.'
          )}
        />
      ) : null}
    </div>
  )
}
